import * as Yup from 'yup';
import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import { useState, useEffect } from 'react';
import useStyles from "../../../../styles/styles";
import { configState } from "../../../../recoil/atoms";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { useNavigate, useLocation } from "react-router-dom";
import MainCard from "../../../../styles/ui-component/cards/MainCard";
import { InventoryApi, AddNewItemRequestInner } from "../../../../api/medical-operations";
import { TextField, Button, Grid, Typography, Divider, CircularProgress } from '@mui/material';

const dateValidation = Yup.date().required('This field is required');
const numberValidation = Yup.number().required('This field is required');
const stringValidation = Yup.string().required('This field is required');
const booleanValidation = Yup.string()
  .required('This field is required')
  .test({
    name: 'is-yes-no',
    test: (value: string | undefined) => {
      if (!value) return false;
      const lowerCaseValue = value.toLowerCase();
      return lowerCaseValue === 'yes' || lowerCaseValue === 'no';
    },
    message: 'Value must be either "yes" or "no"'
  });

const EditItemValidationSchema = (fields: any) => {
  const validationSchema: any = {};
  
  fields.forEach((field: any) => {
    switch (field.DataType) {
      case 'Text':
        validationSchema[field.FieldName] = stringValidation;
        break;
      case 'Yes/No':
        validationSchema[field.FieldName] = booleanValidation;
        break;
      case 'Date':
        validationSchema[field.FieldName] = dateValidation;
        break;
      case 'Number':
        validationSchema[field.FieldName] = numberValidation;
        break;
      default:
        validationSchema[field.FieldName] = stringValidation;
    }
  });
  return Yup.object().shape(validationSchema);
};

interface RowData {
  name: string;
  value: number;  
}

const EditItem = () => {  
  const classes = useStyles();
  const navigate = useNavigate(); 
  const config = useRecoilValue(configState);

  const [fields, setFields] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const location = useLocation();
  const pathname = location.pathname;
  const pathParts = pathname.split('/');
  const collectionId = pathParts[pathParts.length - 1];
  const initialValues = (location.state as { rowData: RowData }).rowData;
  const selectedCollectionName = (location.state as { selectedCollectionName?: string })?.selectedCollectionName;
  const itemID = initialValues["ItemID"];

  const getFields = () => {
    const fields = new InventoryApi(config);
    fields
      .getInventoryCollectionFields(collectionId)
      .then((res) => {        
        setFields(res.data);        
      })
      .catch((err) => {        
        toast.error(err?.response?.data?.detail);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    getFields();
  }, []);

  const editItem = (values: AddNewItemRequestInner[]) => {
    setLoading(true);
    const items = new InventoryApi(config);
    items
      .editInventoryItem(itemID, values)
      .then((res) => {        
        toast.success(res?.data?.Message!);
      })
      .catch((err) => {        
        toast.error(err?.response?.data?.detail);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const convertValue = (value: any, dataType: string): any => {
    return dataType === 'number' ? parseFloat(value) : value;
  };

  const handleSubmit = (values: any) => {
    const formValues: { FieldName: string; Value: any }[] = [];
  
    fields.forEach((field: any) => {
      const fieldName = field.FieldName;
      const value = values[fieldName];
  
      formValues.push({
        FieldName: fieldName,
        Value: convertValue(value, field.DataType),
      });
    });
  
    editItem(formValues);
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      validationSchema={EditItemValidationSchema(fields)}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting }: any) => (
        <Form>
          <MainCard>
            <Typography variant="h5" gutterBottom sx={{ ml: 2, fontSize: '1.2rem' }}>
              Edit Item in <span style={{ color: "blue", fontWeight: 'bold' }}>{selectedCollectionName}</span> Collection
            </Typography>
            <Divider
              variant="middle"
              sx={{ m: 2, width: "90%", borderColor: "rgba(0, 0, 0, 0.30)" }}
            />
            {isLoading ? (
              <Grid
                container
                justifyContent="center"
                alignItems="center"
                style={{ minHeight: '50vh' }}
              >
                <CircularProgress />
              </Grid>
            ) : (
              <>    
                <Grid container spacing={1} sx={{ mt: 1, ml: 0.5 }}>
                  {fields.map((field: any) => (
                    <Grid item key={field.FieldName} sx={{ pr: 0.5 }}>
                      <Field
                        name={field.FieldName}
                        as={TextField}
                        label={field.FieldName}
                        fullWidth
                      />
                      <div style={{ color: 'red' }}>
                        <ErrorMessage
                          name={field.FieldName}
                          component="div"
                        />
                      </div>
                    </Grid>
                  ))}
                  <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end", mt: 4, mr: 6, gap: 1 }}>
                    <Button                                    
                      color="secondary"
                      variant="outlined"
                      className={classes.button} 
                      onClick={() => navigate(-1)}                 
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="secondary"
                      className={classes.button} 
                      disabled={loading}                 
                    >
                      {loading ? <CircularProgress size={20} /> : "Submit"}
                    </Button>
                  </Grid>
                </Grid>
              </>
            )}
          </MainCard>
        </Form>
      )}
    </Formik>
  );
};

export default EditItem;
